import { useApolloClient, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DEACTIVATE_ORGANIZATION_FINDING_POLICY,
  HANDLE_ORGANIZATION_FINDING_POLICY,
  RESUBMIT_ORGANIZATION_FINDING_POLICY,
} from "./queries";
import type { TPolicyStatus } from "./types";

import { GET_FINDINGS_POLICIES } from "../../queries";
import type {
  OrganizationFindingPolicy,
  TreatmentAcceptancePolicy,
} from "gql/graphql";
import { sleep } from "utils/helpers";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

interface IUsePoliciesActions {
  approvePolicy: () => Promise<void>;
  deactivatePolicy: () => Promise<void>;
  rejectPolicy: () => Promise<void>;
  resubmitPolicy: () => Promise<void>;
  submitting: boolean;
  loading: boolean;
}

const tPath = "organization.tabs.policies.findings.";
const DELAY = 500;

const useFindingPoliciesActions = (
  id: string,
  organizationId: string,
  organizationName: string,
  vulnerabilityTreatment: string,
): IUsePoliciesActions => {
  const { t } = useTranslation();
  const [policyStatus, setPolicyStatus] = useState<TPolicyStatus>("APPROVED");
  const client = useApolloClient();

  const [handleOrgFindingPolicy, { loading: handling }] = useMutation(
    HANDLE_ORGANIZATION_FINDING_POLICY,
    {
      onCompleted: (result: {
        handleOrganizationFindingPolicyAcceptance: { success: boolean };
      }): void => {
        if (result.handleOrganizationFindingPolicyAcceptance.success) {
          if (
            policyStatus === "APPROVED" &&
            vulnerabilityTreatment === "ACCEPTED_UNDEFINED"
          ) {
            msgSuccess(
              t(`${tPath}handlePolicies.success.approved.permanentAcceptance`),
              t("sidebar.newOrganization.modal.successTitle"),
            );
          } else if (
            policyStatus === "APPROVED" &&
            vulnerabilityTreatment === "ACCEPTED"
          ) {
            msgSuccess(
              t(`${tPath}handlePolicies.success.approved.temporaryAcceptance`),
              t("sidebar.newOrganization.modal.successTitle"),
            );
          } else {
            msgSuccess(
              t(`${tPath}handlePolicies.success.rejected`),
              t("sidebar.newOrganization.modal.successTitle"),
            );
          }
        }
      },
      onError: ({ message }): void => {
        switch (message) {
          case "Exception - Finding name policy not found":
            msgError(t(`${tPath}errors.notFound`));
            break;
          case "Exception - This policy has already been reviewed":
            msgError(t(`${tPath}errors.alreadyReviewed`));
            break;
          default:
            msgError(t("groupAlerts.errorTextsad"));
            Logger.error("Error handling finding policy", message);
        }
      },
      refetchQueries: [
        { query: GET_FINDINGS_POLICIES, variables: { organizationId } },
      ],
    },
  );

  const [deactivateOrganizationFindingPolicy, { loading: deactivating }] =
    useMutation(DEACTIVATE_ORGANIZATION_FINDING_POLICY, {
      onCompleted: (result: {
        deactivateOrganizationFindingPolicy: { success: boolean };
      }): void => {
        if (result.deactivateOrganizationFindingPolicy.success) {
          if (vulnerabilityTreatment === "ACCEPTED_UNDEFINED") {
            msgSuccess(
              t(`${tPath}deactivatePolicies.success.permanentAcceptance`),
              t("sidebar.newOrganization.modal.successTitle"),
            );
          } else {
            msgSuccess(
              t(`${tPath}deactivatePolicies.success.temporaryAcceptance`),
              t("sidebar.newOrganization.modal.successTitle"),
            );
          }
        }
      },
      onError: ({ message }): void => {
        if (message === "Exception - This policy has already been reviewed") {
          msgError(t(`${tPath}errors.alreadyReviewed`));
        } else {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.warning("Error deactivating finding policy", message);
        }
      },
      refetchQueries: [
        { query: GET_FINDINGS_POLICIES, variables: { organizationId } },
      ],
    });

  const [resubmitOrganizationFindingPolicy, { loading: submitting }] =
    useMutation(RESUBMIT_ORGANIZATION_FINDING_POLICY, {
      onCompleted: (result: {
        submitOrganizationFindingPolicy: { success: boolean };
      }): void => {
        if (result.submitOrganizationFindingPolicy.success) {
          msgSuccess(
            t(`${tPath}addPolicies.success`),
            t("sidebar.newOrganization.modal.successTitle"),
          );
        }
      },
      onError: ({ message }): void => {
        if (message === "Exception - This policy has already been reviewed") {
          msgError(t(`${tPath}errors.alreadyReviewed`));
        } else {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.warning("Error re-submitting finding policy", message);
        }
      },
      refetchQueries: [
        { query: GET_FINDINGS_POLICIES, variables: { organizationId } },
      ],
    });

  const approvePolicy = useCallback(async (): Promise<void> => {
    setPolicyStatus("APPROVED");
    await handleOrgFindingPolicy({
      variables: {
        findingPolicyId: id,
        organizationName,
        status: "APPROVED" as OrganizationFindingPolicy,
        treatmentAcceptance:
          vulnerabilityTreatment as TreatmentAcceptancePolicy,
      },
    });
    await sleep(DELAY);
    await client.refetchQueries({ include: [GET_FINDINGS_POLICIES] });
  }, [
    client,
    handleOrgFindingPolicy,
    id,
    organizationName,
    vulnerabilityTreatment,
  ]);

  const rejectPolicy = useCallback(async (): Promise<void> => {
    setPolicyStatus("REJECTED");
    await handleOrgFindingPolicy({
      variables: {
        findingPolicyId: id,
        organizationName,
        status: "REJECTED" as OrganizationFindingPolicy,
        treatmentAcceptance:
          vulnerabilityTreatment as TreatmentAcceptancePolicy,
      },
    });
    await sleep(DELAY);
    await client.refetchQueries({ include: [GET_FINDINGS_POLICIES] });
  }, [
    client,
    handleOrgFindingPolicy,
    id,
    organizationName,
    vulnerabilityTreatment,
  ]);

  const deactivatePolicy = useCallback(async (): Promise<void> => {
    await deactivateOrganizationFindingPolicy({
      variables: {
        findingPolicyId: id,
        organizationName,
        treatmentAcceptance:
          vulnerabilityTreatment as TreatmentAcceptancePolicy,
      },
    });
    await sleep(DELAY);
    await client.refetchQueries({ include: [GET_FINDINGS_POLICIES] });
  }, [
    client,
    deactivateOrganizationFindingPolicy,
    id,
    organizationName,
    vulnerabilityTreatment,
  ]);

  const resubmitPolicy = useCallback(async (): Promise<void> => {
    await resubmitOrganizationFindingPolicy({
      variables: { findingPolicyId: id, organizationName },
    });
    await sleep(DELAY);
    await client.refetchQueries({ include: [GET_FINDINGS_POLICIES] });
  }, [client, resubmitOrganizationFindingPolicy, id, organizationName]);

  return {
    approvePolicy,
    deactivatePolicy,
    loading: deactivating || handling,
    rejectPolicy,
    resubmitPolicy,
    submitting,
  };
};

export { useFindingPoliciesActions };
